import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, of, catchError } from 'rxjs';
import { environment } from '../../environments/environment';
import { formatDateDDMMYYYY } from '../utils/date.util';
import {
  DashboardItemsResponse,
  RecentUsersResponse,
  DashboardItem,
  RecentUser,
  SummaryCardData,
  DashboardTableData,
  DashboardUserData,
  DashboardTableConfig,
  DashboardApiResponse,
  CombinedDashboardSummary,
} from '../models/dashboard.model';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private apiUrl = `${environment.apiUrl}/Dashboard`;

  constructor(private http: HttpClient) {}

  /**
   * Get recent users with filters
   */
  getRecentUsersWithFilters(
    filters: {
      page?: number;
      perPage?: number;
      sortBy?: string;
      search?: string;
    } = {}
  ): Observable<RecentUsersResponse> {
    let params = new HttpParams();

    if (filters.sortBy) {
      params = params.set('sortBy', filters.sortBy);
    }

    if (filters.search) {
      params = params.set('search', filters.search);
    }

    return this.http.get<any>(`${this.apiUrl}/recentusers`, { params }).pipe(
      map((response) => {
        return {
          data: response.data || response.recentUsers || response,
          total:
            response.total ||
            response.totalCount ||
            (response.data ? response.data.length : 0),
        };
      })
    );
  }

  /**
   * Get dashboard data from the API based on user role
   */
  getDashboardData(userRole?: string): Observable<DashboardApiResponse> {
    // Determine which API endpoint to call based on user role
    const endpoint =
      userRole === 'Admin' ? 'admindashboarditems' : 'dashboarditems';

    return this.http
      .get<DashboardApiResponse>(`${this.apiUrl}/${endpoint}`);
  }

  /**
   * Get admin dashboard data from the API
   */
  getAdminDashboardData(): Observable<DashboardApiResponse> {
    return this.http
      .get<DashboardApiResponse>(`${this.apiUrl}/admindashboarditems`);
  }

  /**
   * Get summary cards data based on user role
   */
  getSummaryCards(userRole: string): Observable<SummaryCardData[]> {
    return this.getDashboardData(userRole).pipe(
      map((response) => {
        if (userRole === 'Admin') {
          // Handle Admin role - use actual API response data
          const summary: CombinedDashboardSummary =
            response.summary || response;

          const cards = [
            {
              title: 'Total Letters',
              value: summary?.totalLetters?.toString() || '0',
              icon: 'pi pi-file',
              iconBgColor: 'bg-blue-100',
              textColor: 'text-blue-600',
            },
            {
              title: 'Total Routing Slips',
              value: summary?.totalRoutingSlips?.toString() || '0',
              icon: 'pi pi-hourglass',
              iconBgColor: 'bg-yellow-100',
              textColor: 'text-yellow-600',
            },
            {
              title: 'Total Managers',
              value: summary?.totalManagers?.toString() || '0',
              icon: 'pi pi-eye',
              iconBgColor: 'bg-green-100',
              textColor: 'text-green-600',
            },
            {
              title: 'Total Secretary',
              value: summary?.totalSecretary?.toString() || '0',
              icon: 'pi pi-check',
              iconBgColor: 'bg-purple-100',
              textColor: 'text-purple-600',
            },
          ];

          return cards;
        } else {
          // For Manager and Secretary roles - use actual API response data
          const summary: CombinedDashboardSummary =
            response.summary || response;

          return [
            {
              title: 'Pending Routing Slips',
              value: summary.pendingRoutingSlips?.toString() || '0',
              icon: 'pi pi-eye',
              iconBgColor: 'bg-red-100',
              textColor: 'text-red-600',
            },
            {
              title: 'Documents to Approve',
              value: summary.documentsToApprove?.toString() || '0',
              icon: 'pi pi-check',
              iconBgColor: 'bg-green-100',
              textColor: 'text-green-600',
            },
            {
              title: 'Pending Letters',
              value: summary.pendingLetters?.toString() || '0',
              icon: 'pi pi-hourglass',
              iconBgColor: 'bg-yellow-100',
              textColor: 'text-yellow-600',
            },
            {
              title: 'Total Documents',
              value: summary.totalDocuments?.toString() || '0',
              icon: 'pi pi-file',
              iconBgColor: 'bg-blue-100',
              textColor: 'text-blue-600',
            },
          ];
        }
      }),
      catchError((error) => {
        // Return empty array on error - no hardcoded data
        return of([]);
      })
    );
  }

  /**
   * Get table configurations
   */
  getTableConfigs(): Observable<{
    letters: DashboardTableConfig;
    routingSlip: DashboardTableConfig;
    users: DashboardTableConfig;
  }> {
    return of({
      letters: {
        title: 'Recent Letters',
        viewAllRoute: '/letters',
        columns: ['Date', 'Document Name', 'Status'],
      },
      routingSlip: {
        title: 'Recent Routing Slips',
        viewAllRoute: '/routing-slip',
        columns: ['Date', 'Document Name', 'Status'],
      },
      users: {
        title: 'Recent Users',
        viewAllRoute: '/users',
        columns: ['Name', 'Email', 'Type', 'Create Date'],
      },
    });
  }

  /**
   * Resolve document/routing-slip id from dashboard API items (supports PascalCase and aliases).
   */
  resolveDocumentId(item: Record<string, unknown> | DashboardItem): string | undefined {
    const keys = [
      'id',
      'Id',
      'routingSlipId',
      'RoutingSlipId',
      'documentId',
      'DocumentId',
    ];
    for (const key of keys) {
      const value = (item as Record<string, unknown>)[key];
      if (value != null && String(value).trim()) {
        return String(value).trim();
      }
    }
    return undefined;
  }

  /**
   * Transform dashboard items to table data format
   */
  transformToTableData(items: DashboardItem[]): DashboardTableData[] {
    return items.map((item) => ({
      uploadDate: formatDateDDMMYYYY(
        item.uploadDate || item.createdDate || item.createdOn
      ),
      documentName: item.documentName || item.title || '',
      status: item.status || 'Pending',
      id: this.resolveDocumentId(item as Record<string, unknown>),
    }));
  }

  /**
   * Transform recent users to user data format
   */
  transformToUserData(users: RecentUser[]): DashboardUserData[] {
    return users.map((user) => ({
      fullName: user.name || user.fullName || '',
      emailAddress: user.email || '',
      userRole: user.type || user.role || 'User',
      createdOn: formatDateDDMMYYYY(
        user.createDate || user.createdAt || user.createdOn
      ),
    }));
  }
}
